import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { InteractivityChecker } from '@angular/cdk/a11y';
import { NonPersistedModalName } from '@shared/models/modal.model';
import { fromEvent } from 'rxjs';
import { LoggerService } from './logger.service';
import { NavService } from './nav.service';

export const MODAL_CLOSE_BUTTON_SELECTOR = 'slotcom-navigation-button[type="close"]';
export const MODAL_ACTION_BUTTON_SELECTOR = 'slotcom-button button';

const GAMEPAD_BUTTON_B = 1;
const GAMEPAD_BUTTON_MENU = 9;

@Injectable({
  providedIn: 'root',
})
export class InputNavigationService {
  private readonly matDialog = inject(MatDialog);
  private readonly interactivityChecker = inject(InteractivityChecker);
  private readonly logger = inject(LoggerService);
  private readonly navService = inject(NavService);

  private readonly gamepadButtonsPressed = new Set<number>();
  private isGamepadPollingStarted = false;

  public init(): void {
    fromEvent<KeyboardEvent>(document, 'keydown').subscribe((e) => {
      if (e.key === 'Escape') this.handleEscapeKey();
      if (e.key === 'Enter') this.handleEnterKey();
      if (e.key === 'Tab') this.handleTabKey(e);
    });

    this.initGamepadSupport();
  }

  private handleEscapeKey(): void {
    const topDialog = this.getTopmostDialog();
    if (!topDialog) return;

    const container = document.getElementById(topDialog.id);
    const closeButton = container?.querySelector<HTMLElement>(MODAL_CLOSE_BUTTON_SELECTOR);
    closeButton?.click();
  }

  private handleEnterKey(): void {
    const topDialog = this.getTopmostDialog();
    if (!topDialog) return;

    const container = document.getElementById(topDialog.id);
    if (!container) return;

    const focusedElement = document.activeElement;
    const isInteractiveElementFocused =
      !!focusedElement?.matches('button, [role="button"]') && container.contains(focusedElement);
    if (isInteractiveElementFocused) return;

    const actionButton = container.querySelector<HTMLButtonElement>(MODAL_ACTION_BUTTON_SELECTOR);
    actionButton?.click();
  }

  private handleTabKey(e: KeyboardEvent): void {
    if (!this.matDialog.openDialogs.length) return;

    const focusedElement = document.activeElement;
    if (this.isFocusInsideTopmostDialog(focusedElement)) return;

    e.preventDefault();
    this.focusTopmostDialog(e.shiftKey);
  }

  private isFocusInsideTopmostDialog(focusedElement: Element | null): boolean {
    const topDialog = this.getTopmostDialog();
    const container = topDialog && document.getElementById(topDialog.id);
    return !!container && !!focusedElement && container.contains(focusedElement);
  }

  private focusTopmostDialog(reverse = false): void {
    const topDialog = this.getTopmostDialog();
    const container = topDialog && document.getElementById(topDialog.id);
    if (!container) return;

    const focusableElements = Array.from(container.querySelectorAll<HTMLElement>('*')).filter((el) =>
      this.interactivityChecker.isTabbable(el)
    );
    const targetElement = reverse ? focusableElements[focusableElements.length - 1] : focusableElements[0];
    targetElement?.focus();
  }

  private getTopmostDialog(): MatDialogRef<unknown> | undefined {
    const openDialogs = this.matDialog.openDialogs;
    if (!openDialogs.length) return undefined;

    return openDialogs.reduce((topmost, dialog) =>
      this.getDialogZIndex(dialog.id) >= this.getDialogZIndex(topmost.id) ? dialog : topmost
    );
  }

  private getDialogZIndex(dialogId: string): number {
    const overlayPane = document.getElementById(dialogId)?.closest<HTMLElement>('.cdk-overlay-pane');
    return overlayPane ? parseInt(getComputedStyle(overlayPane).zIndex, 10) || 0 : 0;
  }

  private initGamepadSupport(): void {
    window.addEventListener('gamepadconnected', (e) => {
      this.logger.debug('[Gamepad] connected', e.gamepad.id, e.gamepad.index);
      this.startGamepadPolling();
    });

    window.addEventListener('gamepaddisconnected', (e) => {
      this.logger.debug('[Gamepad] disconnected', e.gamepad.id, e.gamepad.index);
    });

    this.startGamepadPolling();
  }

  private startGamepadPolling(): void {
    if (this.isGamepadPollingStarted) return;
    this.isGamepadPollingStarted = true;

    this.logger.debug('[Gamepad] polling started');

    const pollGamepad = (): void => {
      const gamepad = navigator.getGamepads().find(Boolean);
      if (gamepad) this.handleGamepadButtons(gamepad);

      requestAnimationFrame(pollGamepad);
    };

    pollGamepad();
  }

  private handleGamepadButtons(gamepad: Gamepad): void {
    const buttonB = this.isButtonNewlyPressed(gamepad, GAMEPAD_BUTTON_B);
    const buttonMenu = this.isButtonNewlyPressed(gamepad, GAMEPAD_BUTTON_MENU);

    if (buttonB) {
      this.logger.debug('[Gamepad] Button B (escape)');
      this.handleEscapeKey();
    }

    if (buttonMenu) {
      const menuIsOpen = !!this.matDialog.getDialogById(NonPersistedModalName.MenuModalComponent);
      this.logger.debug(`[Gamepad] Menu/Start (${menuIsOpen ? 'close' : 'open'} menu)`);
      if (menuIsOpen) {
        this.navService.closeMenu();
      } else {
        this.navService.openMenu();
      }
    }
  }

  private isButtonNewlyPressed(gamepad: Gamepad, buttonIndex: number): boolean {
    const isPressed = !!gamepad.buttons[buttonIndex]?.pressed;
    const wasPressed = this.gamepadButtonsPressed.has(buttonIndex);

    if (isPressed) {
      this.gamepadButtonsPressed.add(buttonIndex);
    } else {
      this.gamepadButtonsPressed.delete(buttonIndex);
    }

    return isPressed && !wasPressed;
  }
}
